/*
 * 0.83 (Paket E): Vorlagen „Finanzen & Verwaltung“ und „Steuererklärung privat“. Sie nutzen die Bausteine
 * aus 0.79–0.82: verbindliche Spaltenregeln (Vier-Augen-Prinzip), Feldtyp Freigabe, Abfrage beim
 * Hineinziehen, Schreibschutz, laufende Nummern, Zahl rechnen, berechnete Felder, Summen und Kennzahlen.
 * Platzhalter wie im übrigen Katalog („col:“, „label:“, „field:“ mit Index).
 */
import type { BuiltinTemplate } from './templateCatalog.js';
import type { AutomationAction, AutomationPredicate, ConditionGroup, FieldTest } from './automation.js';
import type { ColumnGate, FieldConfig } from './types.js';
import type { FormExpr } from './forms.js';
import { DEFAULT_APPROVAL, type ApprovalConfig } from './approval.js';
import { c, cols, done, fieldBecomes, labelsOf, meOn, opts, view, wg } from './templateHelpers.js';

/* ---------- kleine Bausteine ---------- */

const F = (i: number) => `field:${i}`;
const C = (i: number) => `col:${i}`;
/** Verbindliche Regel – Besitzer*innen dürfen mit Begründung übergehen */
const gate = (message: string, ...items: ConditionGroup['items']): ColumnGate => ({ when: { op: 'all', items }, message, ownerOverride: true });
const anyOf = (...items: ConditionGroup['items']): ConditionGroup => ({ op: 'any', items });
const all = (...items: ConditionGroup['items']): ConditionGroup => ({ op: 'all', items });
const field = (i: number, test: FieldTest): AutomationPredicate => ({ type: 'field', fieldId: F(i), test });
const inCols = (...i: number[]): AutomationPredicate => ({ type: 'column', mode: 'in', columnIds: i.map(C) });
const notCreator: AutomationPredicate = { type: 'actor', mode: 'isNot', who: { kind: 'creator' } };
const notApprover = (i: number): AutomationPredicate => ({ type: 'actor', mode: 'isNot', who: { kind: 'approvedBy', fieldId: F(i) } });
const changed = (i: number, to: FieldTest) => ({ type: 'fieldChanged' as const, fieldId: F(i), to });
const move = (col: number, placement: 'top' | 'bottom' = 'bottom'): AutomationAction => ({ type: 'move', columnId: C(col), placement });
/** Nachricht an Hauptverantwortliche, Zuständige und Beobachtende (nie feste Personen) */
const tell = (text: string): AutomationAction => ({ type: 'notify', text, recipients: { owner: true, assignees: true, watchers: true, users: [], groups: [] } });
const approval = (a: Partial<ApprovalConfig>): FieldConfig => ({ approval: { ...DEFAULT_APPROVAL, ...a } });
const ref = (i: number): FormExpr => ({ k: 'ref', q: F(i) });
const formula = (expr: FormExpr): FieldConfig => ({ formula: { expr, result: 'currency' }, aggregate: 'sum' });
const sum = { aggregate: 'sum' } as const;
/** Kennzahl der Boardübersicht über ein Feld dieses Boards */
const metric = (fieldIndex: number, title: string, groupBy: 'none' | 'column' | 'label' | 'month' | 'assignee' = 'column', cards: 'open' | 'all' = 'all', size: 6 | 12 = 6, labels: number[] = []) =>
  wg('metric', size, { agg: 'sum', map: [{ boardId: 'board', fieldId: F(fieldIndex), ...(labels.length ? { labelIds: labels.map((l) => `label:${l}`) } : {}) }], groupBy, cards, target: null }, title);
/** Vier Tätigkeitsbereiche gemeinnütziger Vereine (Steuerrecht) */
const SPHERES = [
  { name: 'Ideeller Bereich', color: 'blue' as const },
  { name: 'Vermögensverwaltung', color: 'sky' as const },
  { name: 'Zweckbetrieb', color: 'green' as const },
  { name: 'Wirtschaftl. Geschäftsbetrieb', color: 'purple' as const },
];

export const FINANCE_TEMPLATES: BuiltinTemplate[] = [
  /* ---------- Kassenprüfung ---------- */
  {
    id: 'builtin:audit',
    name: 'Kassenprüfung',
    category: 'finanzen',
    summary: 'Prüfbereiche von Unterlagen bis Ergebnis – geprüft wird von einer zweiten Person',
    description:
      'Für Kassenprüfer*innen in Vereinen und Organisationen: Jeder Prüfbereich (Bankkonto, Barkasse, Belege eines Bereichs) ist eine Karte mit Prüfcheckliste. Ist die Checkliste fertig, wandert die Karte zur Prüfung; bei einer Beanstandung geht sie mit Frist als Rückfrage an die Kasse. Nach „Geprüft“ darf nur, wer die Karte nicht selbst angelegt hat – und nur mit Ergebnis. Differenzen und geprüfte Belege werden je Spalte summiert.',
    tags: ['Vier-Augen-Prinzip', 'Prüfcheckliste', 'Summen', 'Rückfragen mit Frist'],
    color: '#2b8a3e',
    version: 2,
    adjust: ['Tätigkeitsbereiche (Labels) – passend zu eurer Satzung', 'Frist für Rückfragen an die Kasse: 7 Tage'],
    data: {
      overview: { widgets: [metric(3, 'Geprüfte Belege'), metric(2, 'Differenzen'), wg('deadlines', 12)] },
      views: [view('Beanstandungen', { view: 'list', filter: { labels: labelsOf(4) } }), view('Meine Prüfbereiche', { filter: { assignees: ['me'], completed: 'hide' } })],
      columns: cols(
        { title: 'Unterlagen anfordern' },
        { title: 'Bereit zur Prüfung', entryRule: 'Kassenbuch, Kontoauszüge und Belege liegen vor' },
        { title: 'In Prüfung', wipLimit: 4 },
        { title: 'Rückfrage an die Kasse' },
        {
          title: 'Geprüft',
          color: 'green',
          entryGate: gate(
            'Geprüft wird von einer zweiten Person (nicht von der, die die Karte angelegt hat) – und mit Ergebnis „Ohne Beanstandung“ oder „Mit Hinweisen“.',
            field(0, { op: 'anyOf', optionIds: ['ohne', 'hinweis'] }),
            notCreator,
          ),
        },
      ),
      labels: [...SPHERES, { name: 'Beanstandung', color: 'red' }],
      fields: [
        { name: 'Ergebnis', pinned: true, type: 'select', showOnCard: true, options: opts(['offen', 'Offen', 'gray'], ['ohne', 'Ohne Beanstandung', 'green'], ['hinweis', 'Mit Hinweisen', 'yellow'], ['beanstandung', 'Beanstandung', 'red']) },
        { name: 'Prüfer*in', type: 'person' },
        { name: 'Differenz', pinned: true, type: 'currency', showOnCard: true, config: sum },
        { name: 'Geprüfte Belege', pinned: true, type: 'number', config: { aggregate: 'sum', unit: 'Belege', decimals: 0 } },
        { name: 'Belegnummern', type: 'text' },
      ],
      cardTemplates: [
        {
          name: 'Prüfbereich',
          title: 'Prüfbereich: ',
          description:
            '<p><strong>Zeitraum:</strong> </p><p><strong>Unterlagen:</strong> Kassenbuch, Kontoauszüge, Belege</p><p><strong>Stichprobe:</strong> </p><p><strong>Feststellungen:</strong> </p>',
          checklist: ['Anfangs- und Endbestand stimmen', 'Belege lückenlos und nummeriert', 'Kontoauszüge abgeglichen', 'Ausgaben durch Beschlüsse gedeckt', 'Stichprobe geprüft'],
        },
      ],
      automations: [
        { name: 'Unterlagen vollständig → bereit zur Prüfung', trigger: { type: 'checklistDone' }, when: all(inCols(0)), actions: [move(1)] },
        meOn(2, 'Prüfung beginnt: mir zuweisen'),
        {
          name: 'Beanstandung → Rückfrage an die Kasse (7 Tage)',
          trigger: fieldBecomes(0, 'beanstandung'),
          actions: [move(3, 'top'), { type: 'addLabel', labelId: 'label:4' }, { type: 'setDue', days: 7 }, tell('Bei der Kassenprüfung gibt es eine Rückfrage an die Kasse.')],
        },
        {
          name: 'Ergebnis eingetragen (zweite Person) → Geprüft',
          trigger: changed(0, { op: 'anyOf', optionIds: ['ohne', 'hinweis'] }),
          when: all(notCreator),
          actions: [move(4, 'top')],
        },
        done(4),
      ],
      cards: [
        c(0, 'Kontoauszüge Januar bis Dezember', [0], { checklist: ['Girokonto', 'Tagesgeld', 'PayPal'] }),
        c(1, 'Barkasse: Kassenbuch und Zählprotokoll', [0], { checklist: ['Kassenbuch', 'Zählprotokoll 31.12.', 'Quittungsblock'] }),
        c(2, 'Belege Sommerfest', [3], { custom: { [F(3)]: 48, [F(4)]: '112–160' }, checklist: ['Einnahmen Getränke', 'Einkauf', 'Musik und GEMA'] }),
        c(3, 'Fehlender Beleg Getränkeeinkauf', [2, 4], { dueInDays: 7, custom: { [F(0)]: 'beanstandung', [F(2)]: -23.4 } }),
        c(4, 'Mitgliedsbeiträge', [0], { custom: { [F(0)]: 'ohne', [F(3)]: 112 } }),
      ],
    },
  },

  /* ---------- Rechnungseingang ---------- */
  {
    id: 'builtin:invoices',
    name: 'Rechnungseingang',
    category: 'finanzen',
    summary: 'Eingang, sachliche Prüfung, Freigabe, Zahlung – mit Vier-Augen-Prinzip und Skonto',
    description:
      'Eingangsrechnungen von der Post bis zur Ablage: Wer die Rechnung sachlich prüft, hakt „Sachlich richtig“ ab; freigeben darf jemand anderes, ab 1.000 € braucht es eine zweite Freigabe. Ändert sich der Betrag oder die Datei, verfällt die Freigabe. Bezahlen darf nicht, wer freigegeben hat; beim Verschieben nach „Bezahlt“ fragt KaBa nach dem Zahltag. Jede Rechnung bekommt eine Belegnummer, Skontofristen erinnern zwei Arbeitstage vorher, „Abgelegt“ ist schreibgeschützt.',
    tags: ['Vier-Augen-Prinzip', 'Freigabe ab Betrag', 'Belegnummer', 'Skonto-Erinnerung', 'Formular'],
    color: '#1864ab',
    version: 4,
    adjust: ['Zweite Freigabe ab 1.000 €', 'Skonto-Erinnerung 2 Arbeitstage vorher', 'Belegnummer ER-Jahr-0001'],
    data: {
      overview: { widgets: [metric(2, 'Offene Rechnungen', 'column', 'open', 12), wg('deadlines', 6), wg('tasks', 6, { rows: 10, sort: 'due' }, 'Ohne Zuständige', { assignees: ['none'] })] },
      views: [view('Offen nach Fälligkeit', { view: 'list', filter: { completed: 'hide' }, sort: 'due' }), view('Meine Prüfungen', { filter: { assignees: ['me'], completed: 'hide' } })],
      columns: cols(
        { title: 'Eingang' },
        { title: 'Sachlich prüfen' },
        { title: 'Freigabe', entryGate: gate('Erst sachlich prüfen: „Sachlich richtig“ abhaken.', field(5, { op: 'checked' })) },
        { title: 'Zur Zahlung', entryGate: gate('Die Rechnung braucht eine Freigabe.', field(6, { op: 'approved' })) },
        {
          title: 'Bezahlt',
          color: 'green',
          entryGate: gate('Wer freigegeben hat, bezahlt nicht selbst (Vier-Augen-Prinzip).', notApprover(6)),
          entryAsk: { fieldIds: [F(7)], required: [F(7)] },
        },
        { title: 'Abgelegt', locked: true },
      ),
      labels: [
        { name: 'Dringend', color: 'red' },
        { name: 'Skonto', color: 'green' },
        { name: 'Dauerauftrag', color: 'blue' },
        { name: 'Rückfrage', color: 'yellow' },
      ],
      fields: [
        { name: 'Rechnungsnr.', type: 'text' },
        { name: 'Lieferant', type: 'text', showOnCard: true },
        { name: 'Betrag', pinned: true, type: 'currency', showOnCard: true, config: sum },
        { name: 'Skonto bis', type: 'date' },
        { name: 'Kostenstelle', type: 'text' },
        { name: 'Sachlich richtig', pinned: true, type: 'checkbox' },
        { name: 'Freigabe', pinned: true, type: 'approval', showOnCard: true, config: approval({ notCreator: true, second: { fieldId: F(2), above: 1000 }, resetFields: [F(2)], resetFiles: true }) },
        { name: 'Bezahlt am', type: 'date' },
        { name: 'Belegnr.', type: 'sequence', showOnCard: true, config: { sequence: { prefix: 'ER-{JJJJ}-', digits: 4, yearly: true } } },
      ],
      forms: [{ template: 'rechnung', column: 0, title: 'Rechnung einreichen' }],
      // 0.90 (K5, Pilot „Geregelter Rechnungseingang“): der geregelte Ablauf steht in „Worum es geht“; was für den
      // nächsten Schritt fehlt, zeigt der Hinweis an der Karte aus denselben Spaltenregeln
      context: {
        description:
          'Geregelter Rechnungseingang: Eingang → Sachlich prüfen → Freigabe → Zur Zahlung → Bezahlt → Abgelegt.\n• In „Freigabe“ erst mit „Sachlich richtig“.\n• In „Zur Zahlung“ erst mit Freigabe – die gibt nicht, wer die Karte angelegt hat; ab 1.000 € braucht es eine zweite Freigabe. Ändert sich Betrag oder Rechnungsdatei, verfällt die Freigabe.\n• Nach „Bezahlt“ nicht durch die Person, die freigegeben hat (Vier-Augen-Prinzip); KaBa fragt nach dem Zahltag.\n• „Abgelegt“ ist schreibgeschützt.',
        outcome: 'Jede Rechnung ist sachlich geprüft, freigegeben, fristgerecht bezahlt und abgelegt',
        refs: [],
      },
      automations: [
        { name: 'Per Formular mit Rechnung eingegangen → sachlich prüfen', trigger: { type: 'created', columnId: C(0) }, when: all({ type: 'files', kind: 'any', includeComments: false, min: 1 }), actions: [move(1)] },
        { name: 'Rechnung angehängt → sachlich prüfen', trigger: { type: 'fileAdded', kind: 'any', includeComments: false }, when: all(inCols(0)), actions: [move(1)] },
        meOn(1, 'Sachliche Prüfung: mir zuweisen'),
        { name: 'Sachlich richtig → zur Freigabe', trigger: changed(5, { op: 'checked' }), when: all(inCols(0, 1)), actions: [move(2), tell('Eine Rechnung wartet auf Freigabe.')] },
        { name: 'Freigegeben → zur Zahlung', trigger: changed(6, { op: 'approved' }), when: all(inCols(2)), actions: [move(3)] },
        {
          name: 'Abgelehnt → zurück zur sachlichen Prüfung',
          trigger: changed(6, { op: 'rejected' }),
          actions: [move(1, 'top'), { type: 'clearField', fieldId: F(5) }, { type: 'addLabel', labelId: 'label:3' }, tell('Eine Rechnung wurde bei der Freigabe abgelehnt.')],
        },
        {
          name: 'Skonto: zwei Arbeitstage vorher erinnern',
          trigger: { type: 'dateReached', which: 'due', fieldId: F(3), offset: { mode: 'workdays', days: -2, time: '09:00' } },
          when: all(inCols(0, 1, 2, 3)),
          actions: [{ type: 'addLabel', labelId: 'label:1' }, tell('Die Skontofrist läuft in zwei Arbeitstagen ab.')],
        },
        { name: 'Bezahlt → abhaken', trigger: { type: 'moved', columnId: C(4) }, actions: [{ type: 'complete' }] },
      ],
      cards: [
        c(0, 'Stadtwerke – Strom Oktober', [2], { custom: { [F(1)]: 'Stadtwerke', [F(2)]: 184.2 } }),
        c(1, 'Druckerei Müller – Flyer Herbst', [], { custom: { [F(0)]: '2026-1187', [F(1)]: 'Druckerei Müller', [F(2)]: 1450, [F(4)]: 'Öffentlichkeitsarbeit' } }),
        c(1, 'Getränkehandel – Sommerfest', [1], { dueInDays: 10, custom: { [F(1)]: 'Getränke Huber', [F(2)]: 612.75, [F(4)]: 'Sommerfest' } }),
        c(2, 'Büromaterial', [], { custom: { [F(1)]: 'Bürobedarf Schmidt', [F(2)]: 86.4, [F(5)]: true } }),
      ],
    },
  },

  /* ---------- Ausgangsrechnungen & Mahnwesen ---------- */
  {
    id: 'builtin:dunning',
    name: 'Ausgangsrechnungen & Mahnwesen',
    category: 'finanzen',
    summary: 'Rechnungsnummern, Zahlungsziel, Mahnstufen mit Gebühren – offene Posten im Blick',
    description:
      'Für Rechnungen, die ihr stellt: Jede Karte bekommt fortlaufend eine Rechnungsnummer. Wird die Rechnung gestellt, gilt ein Zahlungsziel von 14 Tagen. Ist sie überfällig, erhöht KaBa die Mahnstufe, rechnet 5 € Mahngebühr dazu und setzt eine neue Frist; nach der dritten Mahnung geht die Karte zur Klärung. „Offen gesamt“ rechnet Betrag plus Gebühren, Summen zeigen die offenen Posten je Spalte. Beim Verschieben nach „Bezahlt“ fragt KaBa nach dem Zahlungseingang.',
    tags: ['Rechnungsnummer', 'Mahnstufen', 'Berechnetes Feld', 'Offene Posten'],
    color: '#c92a2a',
    version: 2,
    adjust: ['Zahlungsziel 14 Tage', 'Mahngebühr 5 € je Stufe, neue Frist 10 Tage', 'Klärung nach der 3. Mahnung', 'Rechnungsnummer RE-Jahr-0001'],
    data: {
      overview: { widgets: [metric(4, 'Offene Posten', 'column', 'open', 12), metric(2, 'Umsatz nach Monat', 'month', 'all', 12)] },
      views: [view('Offene Posten', { view: 'list', filter: { completed: 'hide' }, sort: 'due' })],
      columns: cols(
        { title: 'Zu fakturieren' },
        { title: 'Rechnung gestellt', entryGate: gate('Ohne Betrag keine Rechnung: Bitte „Betrag“ eintragen.', field(2, { op: 'filled' })) },
        { title: 'Gemahnt' },
        { title: 'Klärung', exitRule: 'Ratenzahlung, Inkasso oder Ausbuchung entschieden' },
        { title: 'Bezahlt', color: 'green', entryAsk: { fieldIds: [F(6)], required: [F(6)] } },
      ),
      labels: [
        { name: 'Teilzahlung', color: 'yellow' },
        { name: 'Gutschrift', color: 'blue' },
        { name: 'Klärung', color: 'red' },
      ],
      fields: [
        { name: 'Rechnungsnr.', type: 'sequence', showOnCard: true, config: { sequence: { prefix: 'RE-{JJJJ}-', digits: 4, yearly: true } } },
        { name: 'Kunde', pinned: true, type: 'text', showOnCard: true },
        { name: 'Betrag', type: 'currency', config: sum },
        { name: 'Mahngebühren', type: 'currency', config: sum },
        { name: 'Offen gesamt', pinned: true, type: 'formula', showOnCard: true, config: formula({ k: 'op', op: '+', a: ref(2), b: ref(3) }) },
        { name: 'Mahnstufe', pinned: true, type: 'number', showOnCard: true, config: { decimals: 0 } },
        { name: 'Zahlungseingang am', type: 'date' },
      ],
      automations: [
        { name: 'Rechnung gestellt: Zahlungsziel 14 Tage', trigger: { type: 'moved', columnId: C(1) }, actions: [{ type: 'setDue', days: 14 }] },
        {
          name: 'Rechnung gestellt: Mahnstufe und Gebühren starten bei 0',
          trigger: { type: 'moved', columnId: C(1) },
          when: all(field(5, { op: 'empty' })),
          actions: [
            { type: 'calcField', fieldId: F(5), op: 'set', value: 0 },
            { type: 'calcField', fieldId: F(3), op: 'set', value: 0 },
          ],
        },
        // Zuerst prüfen, ob schon dreimal gemahnt wurde – sonst würde die dritte Mahnung sofort zur Klärung führen
        {
          name: 'Nach der dritten Mahnung überfällig → Klärung',
          trigger: { type: 'overdue' },
          when: all(inCols(2), field(5, { op: 'gte', value: 3 })),
          actions: [move(3, 'top'), { type: 'addLabel', labelId: 'label:2' }, tell('Auch nach der dritten Mahnung ist nicht bezahlt – bitte klären.')],
        },
        {
          name: 'Überfällig → mahnen (Stufe +1, 5 € Gebühr, 10 Tage)',
          trigger: { type: 'overdue' },
          when: all(inCols(1, 2), field(5, { op: 'lt', value: 3 })),
          actions: [
            move(2, 'top'),
            { type: 'calcField', fieldId: F(5), op: 'add', value: 1 },
            { type: 'calcField', fieldId: F(3), op: 'add', value: 5 },
            { type: 'setDue', days: 10 },
            tell('Eine Rechnung ist überfällig – die Mahnstufe wurde erhöht. Bitte Mahnung verschicken.'),
          ],
        },
        { name: 'Bezahlt → abhaken', trigger: { type: 'moved', columnId: C(4) }, actions: [{ type: 'complete' }, { type: 'clearDue' }] },
      ],
      cards: [
        c(0, 'Workshop Teamtag', [], { custom: { [F(1)]: 'Stadtbibliothek', [F(2)]: 780 } }),
        c(1, 'Saalmiete Oktober', [], { dueInDays: 9, custom: { [F(1)]: 'Musikschule', [F(2)]: 350, [F(3)]: 0, [F(5)]: 0 } }),
        c(2, 'Standgebühr Herbstmarkt', [], { dueInDays: 4, custom: { [F(1)]: 'Café Sonnenkorn', [F(2)]: 120, [F(3)]: 5, [F(5)]: 1 } }),
        c(4, 'Anzeige Festschrift', [], { custom: { [F(1)]: 'Autohaus Berg', [F(2)]: 450, [F(3)]: 0, [F(5)]: 0 } }),
      ],
    },
  },

  /* ---------- Auslagen & Reisekosten ---------- */
  {
    id: 'builtin:expenses',
    name: 'Auslagen & Reisekosten',
    category: 'finanzen',
    summary: 'Auslagen einreichen, Beleg prüfen, freigeben, erstatten – Fahrtkosten rechnet KaBa',
    description:
      'Wer etwas vorgestreckt hat, reicht es per Formular oder als Karte ein: Betrag und Beleg, bei Fahrten die Kilometer – die Fahrtkosten (0,30 € je km) rechnet KaBa. Ohne Beleg geht die Karte in die Rückfrage. Freigeben darf nicht, wer eingereicht hat; ab 500 € braucht es zwei Freigaben. Auszahlen darf nicht, wer freigegeben hat, und beim Verschieben nach „Erstattet“ fragt KaBa nach dem Auszahlungstag.',
    tags: ['Vier-Augen-Prinzip', 'Kilometerpauschale', 'Formular', 'Belegpflicht'],
    color: '#e8590c',
    version: 3,
    adjust: ['Kilometerpauschale 0,30 € (Formel „Fahrtkosten“)', 'Zweite Freigabe ab 500 €'],
    data: {
      overview: { widgets: [metric(0, 'Offene Auslagen', 'column', 'open'), metric(2, 'Fahrtkosten', 'column', 'open'), metric(0, 'Erstattet nach Monat', 'month', 'all', 12)] },
      columns: cols(
        { title: 'Eingereicht' },
        { title: 'Rückfrage' },
        {
          title: 'Prüfung & Freigabe',
          entryGate: { when: anyOf({ type: 'files', kind: 'any', includeComments: false, min: 1 }, field(1, { op: 'filled' })), message: 'Bitte einen Beleg anhängen (bei Fahrten genügen die Kilometer).', ownerOverride: true },
        },
        { title: 'Zur Auszahlung', entryGate: gate('Die Auslage braucht eine Freigabe.', field(4, { op: 'approved' })) },
        {
          title: 'Erstattet',
          color: 'green',
          entryGate: gate('Wer freigegeben hat, zahlt nicht selbst aus (Vier-Augen-Prinzip).', notApprover(4)),
          entryAsk: { fieldIds: [F(5)], required: [F(5)] },
        },
      ),
      labels: [
        { name: 'Reisekosten', color: 'blue' },
        { name: 'Bewirtung', color: 'orange' },
        { name: 'Material', color: 'green' },
        { name: 'Beleg fehlt', color: 'red' },
      ],
      fields: [
        { name: 'Betrag', pinned: true, type: 'currency', showOnCard: true, config: sum },
        { name: 'Kilometer', type: 'number', config: { aggregate: 'sum', unit: 'km', decimals: 0 } },
        { name: 'Fahrtkosten', pinned: true, type: 'formula', showOnCard: true, config: formula({ k: 'op', op: '*', a: ref(1), b: { k: 'num', v: 0.3 } }) },
        { name: 'Anlass', type: 'text' },
        { name: 'Freigabe', pinned: true, type: 'approval', showOnCard: true, config: approval({ notCreator: true, second: { fieldId: F(0), above: 500 }, resetFields: [F(0), F(1)], resetFiles: true }) },
        { name: 'Ausgezahlt am', type: 'date' },
        { name: 'Kostenstelle', type: 'text' },
      ],
      forms: [{ template: 'auslage', column: 0, title: 'Auslage einreichen' }],
      cardTemplates: [
        { name: 'Auslage', title: 'Auslage: ', description: '<p><strong>Wofür:</strong> </p><p>Beleg als Datei anhängen.</p>', assignCreator: false },
        { name: 'Dienstfahrt', title: 'Fahrt: ', labelIds: ['label:0'], description: '<p><strong>Von – nach:</strong> </p><p><strong>Anlass:</strong> </p><p>Kilometer ins Feld „Kilometer“ – die Fahrtkosten rechnet KaBa.</p>' },
      ],
      automations: [
        {
          name: 'Ohne Beleg eingereicht → Rückfrage',
          trigger: { type: 'created', columnId: C(0) },
          when: all({ type: 'files', kind: 'any', includeComments: false, min: 0 }, field(1, { op: 'empty' })),
          actions: [move(1), { type: 'addLabel', labelId: 'label:3' }],
        },
        { name: 'Beleg nachgereicht → Prüfung', trigger: { type: 'fileAdded', kind: 'any', includeComments: false }, when: all(inCols(0, 1)), actions: [{ type: 'removeLabel', labelId: 'label:3' }, move(2)] },
        { name: 'Freigegeben → zur Auszahlung', trigger: changed(4, { op: 'approved' }), when: all(inCols(2)), actions: [move(3)] },
        { name: 'Abgelehnt → Rückfrage', trigger: changed(4, { op: 'rejected' }), actions: [move(1, 'top'), tell('Eine Auslage wurde nicht freigegeben – bitte die Begründung im Verlauf ansehen.')] },
        { name: 'Erstattet → abhaken', trigger: { type: 'moved', columnId: C(4) }, actions: [{ type: 'complete' }] },
      ],
      cards: [
        c(0, 'Fahrt zum Landesverbandstreffen', [0], { custom: { [F(1)]: 148, [F(3)]: 'Landesverbandstreffen' } }),
        c(1, 'Blumen für die Jubilarin', [1, 3], { custom: { [F(0)]: 24.9, [F(3)]: 'Ehrung' } }),
        c(2, 'Bastelmaterial Kindergruppe', [2], { custom: { [F(0)]: 63.15, [F(3)]: 'Kindergruppe' } }),
      ],
    },
  },

  /* ---------- Monatsabschluss ---------- */
  {
    id: 'builtin:monthclose',
    name: 'Monatsabschluss',
    category: 'finanzen',
    summary: 'Wiederkehrende Abschlussaufgaben mit Kontrolle durch eine zweite Person',
    description:
      'Die Aufgaben, die jeden Monat anstehen – Bankabstimmung, Kasse, offene Posten, Lohn, Umsatzsteuer-Voranmeldung – kommen als wiederkehrende Karten mit Checkliste. Ist die Checkliste fertig, geht die Karte zur Kontrolle; abschließen darf nur jemand anderes als die Person, die sie zur Kontrolle gegeben hat. Gefundene Abweichungen werden summiert.',
    tags: ['Wiederkehrend', 'Vier-Augen-Kontrolle', 'Checklisten', 'Mit Beispielkarten'],
    color: '#5f3dc4',
    version: 2,
    cardsDefault: true,
    adjust: ['Fristen der wiederkehrenden Aufgaben (Tag im Monat)'],
    data: {
      overview: { widgets: [wg('deadlines', 12), metric(0, 'Abweichungen', 'month', 'all', 12)] },
      columns: cols(
        { title: 'Anstehend' },
        { title: 'In Arbeit' },
        { title: 'Zur Kontrolle' },
        {
          title: 'Erledigt',
          color: 'green',
          entryGate: gate('Kontrolle durch eine zweite Person: Wer die Aufgabe zur Kontrolle gegeben hat, schließt sie nicht selbst ab.', {
            type: 'actor',
            mode: 'isNot',
            who: { kind: 'movedTo', columnId: C(2) },
          }),
        },
      ),
      labels: [
        { name: 'Bank', color: 'blue' },
        { name: 'Kasse', color: 'green' },
        { name: 'Lohn', color: 'purple' },
        { name: 'Steuer', color: 'orange' },
      ],
      fields: [{ name: 'Abweichung', pinned: true, type: 'currency', showOnCard: true, config: sum }],
      automations: [
        meOn(1),
        { name: 'Checkliste fertig → zur Kontrolle', trigger: { type: 'checklistDone' }, when: all(inCols(0, 1)), actions: [move(2), tell('Eine Abschlussaufgabe wartet auf Kontrolle.')] },
        done(3),
      ],
      cards: [
        c(0, 'Bankkonten abstimmen', [0], { dueInDays: 5, checklist: ['Kontoauszüge importiert', 'Buchungen zugeordnet', 'Saldo stimmt'], recurrence: { freq: 'monthly', interval: 1, basis: 'due' } }),
        c(0, 'Kasse zählen und Kassenbuch abschließen', [1], { dueInDays: 3, checklist: ['Bestand gezählt', 'Kassenbuch fortgeschrieben', 'Belege abgelegt'], recurrence: { freq: 'monthly', interval: 1, basis: 'due' } }),
        c(0, 'Offene Posten prüfen', [0], { dueInDays: 7, checklist: ['Eingangsrechnungen fällig?', 'Ausgangsrechnungen überfällig?', 'Mahnungen verschickt'], recurrence: { freq: 'monthly', interval: 1, basis: 'due' } }),
        c(0, 'Lohnabrechnung kontrollieren', [2], { dueInDays: 8, checklist: ['Stunden und Zuschläge', 'Ein- und Austritte', 'Überweisungen'], recurrence: { freq: 'monthly', interval: 1, basis: 'due' } }),
        c(0, 'Umsatzsteuer-Voranmeldung', [3], { dueInDays: 10, checklist: ['Umsätze abgestimmt', 'Vorsteuer geprüft', 'Übermittelt'], recurrence: { freq: 'monthly', interval: 1, basis: 'due' } }),
      ],
    },
  },

  /* ---------- Jahresabschluss & Steuern ---------- */
  {
    id: 'builtin:yearclose',
    name: 'Jahresabschluss & Steuern',
    category: 'finanzen',
    summary: 'Vom Inventar bis zur Abgabe beim Finanzamt – mit Freigabe durch den Vorstand',
    description:
      'Der Jahresabschluss als Fahrplan rückwärts von der Abgabefrist: Beim Einrichten gibst du die Frist an, die Beispielkarten bekommen ihre Termine davor. Was fehlt, sammelt „Unterlagen fehlen“. Ans Steuerbüro geht eine Karte erst mit fertiger Checkliste; abgeschlossen ist sie erst mit Freigabe – und die gibt nicht, wer zuständig war.',
    tags: ['Stichtag', 'Freigabe', 'Abhängigkeiten', 'Steuerbüro'],
    color: '#364fc7',
    version: 2,
    adjust: ['Abstände der Aufgaben zur Abgabefrist', 'Erinnerung 3 Tage vor „Unterlagen bis“'],
    data: {
      setup: { label: 'Abgabefrist', help: 'Bis wann müssen Abschluss und Steuererklärungen beim Finanzamt sein? Die Aufgaben bekommen ihre Fristen davor.' },
      overview: { widgets: [wg('deadlines', 12), wg('blocked', 6, { rows: 10 }), wg('ready', 6, { rows: 10 })] },
      views: [view('Zeitplan', { view: 'timeline' })],
      columns: cols(
        { title: 'Vorbereiten' },
        { title: 'Unterlagen fehlen' },
        { title: 'In Arbeit', wipLimit: 5 },
        { title: 'Beim Steuerbüro', entryGate: gate('Erst die Checkliste abarbeiten – dann geht es ans Steuerbüro.', { type: 'checklist', op: 'complete' }) },
        { title: 'Freigabe' },
        { title: 'Abgeschlossen', color: 'green', entryGate: gate('Der Abschluss braucht eine Freigabe.', field(0, { op: 'approved' })) },
      ),
      labels: [
        { name: 'Bilanz / EÜR', color: 'blue' },
        { name: 'Körperschaftsteuer', color: 'purple' },
        { name: 'Umsatzsteuer', color: 'orange' },
        { name: 'Gemeinnützigkeit', color: 'green' },
      ],
      fields: [
        { name: 'Freigabe', pinned: true, type: 'approval', showOnCard: true, config: approval({ notCreator: false, notAssignee: true, resetFiles: true }) },
        { name: 'Ansprechperson Steuerbüro', type: 'text' },
        { name: 'Unterlagen bis', pinned: true, type: 'date' },
      ],
      automations: [
        meOn(2),
        { name: 'Zur Freigabe: Verantwortliche informieren', trigger: { type: 'moved', columnId: C(4) }, actions: [tell('Ein Teil des Jahresabschlusses wartet auf Freigabe.')] },
        { name: 'Freigegeben → abgeschlossen', trigger: changed(0, { op: 'approved' }), when: all(inCols(4)), actions: [move(5, 'top')] },
        { name: 'Abgelehnt → zurück in Arbeit', trigger: changed(0, { op: 'rejected' }), actions: [move(2, 'top'), tell('Die Freigabe wurde abgelehnt – bitte die Begründung im Verlauf ansehen.')] },
        {
          name: 'Unterlagen bis: drei Tage vorher erinnern',
          trigger: { type: 'dateReached', which: 'due', fieldId: F(2), offset: { mode: 'calendar', days: -3, time: '09:00' } },
          when: all(inCols(0, 1)),
          actions: [tell('In drei Tagen müssen die Unterlagen beim Steuerbüro sein.')],
        },
        done(5),
      ],
      cards: [
        c(0, 'Inventur und Bestandsliste', [0], { dueFromSetup: -75, checklist: ['Kasse', 'Lager', 'Anlagen'] }),
        c(0, 'Konten zum 31.12. abstimmen', [0], { dueFromSetup: -70, checklist: ['Bank', 'Kasse', 'Forderungen', 'Verbindlichkeiten'] }),
        c(1, 'Spendenbelege und Zuwendungsliste', [3], { dueFromSetup: -60 }),
        c(0, 'Belege an das Steuerbüro', [0], { dueFromSetup: -55, after: [0, 1, 2], checklist: ['Buchhaltung exportiert', 'Belege vollständig', 'Fragen notiert'] }),
        c(0, 'Abschluss prüfen und freigeben', [0], { dueFromSetup: -14, after: [3] }),
        c(0, 'Steuererklärungen abgeben', [1, 2], { dueFromSetup: 0, after: [4] }),
      ],
    },
  },

  /* ---------- Haushalts- und Budgetplanung ---------- */
  {
    id: 'builtin:budget',
    name: 'Haushalts- & Budgetplanung',
    category: 'finanzen',
    summary: 'Plan, Ist und Abweichung je Posten – Summen je Spalte, Übersicht mit Kennzahlen',
    description:
      'Jeder Haushaltsposten ist eine Karte mit Plan und Ist; die Abweichung rechnet KaBa. Posten wandern vom Entwurf über die Abstimmung zum Beschluss und werden im laufenden Jahr mit Ist-Werten gefüllt. Summen stehen im Spaltenkopf, die Boardübersicht zeigt Plan und Ist getrennt für Einnahmen und Ausgaben. Geht ein Posten über den Plan, bekommt er ein Label und die Verantwortlichen eine Nachricht.',
    tags: ['Plan/Ist', 'Berechnetes Feld', 'Summen', 'Kennzahlen'],
    color: '#087f5b',
    version: 3,
    adjust: ['Kategorien Einnahme/Ausgabe (Labels) – jeder Posten braucht genau eines'],
    data: {
      overview: {
        widgets: [
          metric(0, 'Einnahmen – Plan', 'column', 'all', 6, [0]),
          metric(1, 'Einnahmen – Ist', 'column', 'all', 6, [0]),
          metric(0, 'Ausgaben – Plan', 'column', 'all', 6, [1]),
          metric(1, 'Ausgaben – Ist', 'column', 'all', 6, [1]),
          metric(2, 'Ausgaben – Abweichung (über Plan positiv)', 'column', 'all', 12, [1]),
        ],
      },
      views: [view('Einnahmen', { view: 'table', filter: { labels: labelsOf(0) } }), view('Ausgaben', { view: 'table', filter: { labels: labelsOf(1) } }), view('Über Plan', { view: 'list', filter: { labels: labelsOf(2) } })],
      columns: cols(
        { title: 'Entwurf' },
        { title: 'Abstimmung' },
        { title: 'Beschlossen', entryGate: gate('Ein Posten braucht einen Planwert, bevor er beschlossen wird.', field(0, { op: 'filled' })) },
        { title: 'Laufend' },
        { title: 'Abgerechnet', color: 'green', entryGate: gate('Zum Abrechnen bitte den Ist-Wert eintragen.', field(1, { op: 'filled' })) },
      ),
      labels: [
        { name: 'Einnahme', color: 'green' },
        { name: 'Ausgabe', color: 'orange' },
        { name: 'Über Plan', color: 'red' },
      ],
      fields: [
        { name: 'Plan', pinned: true, type: 'currency', showOnCard: true, config: sum },
        { name: 'Ist', pinned: true, type: 'currency', showOnCard: true, config: sum },
        { name: 'Abweichung', pinned: true, type: 'formula', showOnCard: true, config: formula({ k: 'op', op: '-', a: ref(1), b: ref(0) }) },
        { name: 'Kostenstelle', type: 'text' },
      ],
      automations: [
        {
          name: 'Ausgabe über Plan → Label und Nachricht',
          trigger: { type: 'fieldChanged', fieldId: F(1) },
          when: all({ type: 'labels', mode: 'any', labelIds: labelsOf(1) }, field(2, { op: 'gt', value: 0 })),
          actions: [{ type: 'addLabel', labelId: 'label:2' }, tell('Ein Haushaltsposten liegt über dem Plan.')],
        },
        { name: 'Wieder im Plan → Label weg', trigger: { type: 'fieldChanged', fieldId: F(1) }, when: all(field(2, { op: 'lte', value: 0 })), actions: [{ type: 'removeLabel', labelId: 'label:2' }] },
        done(4),
      ],
      cards: [
        c(0, 'Mitgliedsbeiträge', [0], { custom: { [F(0)]: 18000 } }),
        c(1, 'Zuschuss Stadt', [0], { custom: { [F(0)]: 5000 } }),
        c(2, 'Hallenmiete', [1], { custom: { [F(0)]: 6400, [F(3)]: 'Sport' } }),
        c(3, 'Übungsleitungen', [1], { custom: { [F(0)]: 9600, [F(1)]: 7200, [F(3)]: 'Sport' } }),
        c(3, 'Versicherungen', [1, 2], { custom: { [F(0)]: 1200, [F(1)]: 1340 } }),
        c(4, 'Sommerfest', [0], { custom: { [F(0)]: 2500, [F(1)]: 3120.5 } }),
      ],
    },
  },

  /* ---------- Spenden & Zuwendungsbestätigungen ---------- */
  {
    id: 'builtin:donations',
    name: 'Spenden & Zuwendungsbestätigungen',
    category: 'finanzen',
    summary: 'Spendeneingang, Bestätigung mit fortlaufender Nummer, Versand und Dank',
    description:
      'Für gemeinnützige Organisationen: Jede Spende ist eine Karte mit Betrag, Eingangsdatum und Art; die Bestätigungsnummer vergibt KaBa fortlaufend je Jahr. Über 300 € markiert KaBa die Bestätigung als Pflicht, Sachspenden bekommen eine Checkliste für den Wertnachweis. Versendet ist eine Bestätigung erst mit angehängtem PDF; „Abgelegt“ ist schreibgeschützt.',
    tags: ['Laufende Nummer', 'Summen', 'Sachspenden', 'Schreibschutz'],
    color: '#a61e4d',
    version: 2,
    adjust: ['Grenze für die Bestätigungspflicht: 300 €', 'Großspende ab 1.000 €', 'Bestätigungsnummer ZB-Jahr-001'],
    data: {
      overview: { widgets: [metric(1, 'Spenden nach Monat', 'month', 'all', 12), metric(1, 'Spenden nach Art', 'label')] },
      views: [view('Bestätigung fällig', { view: 'list', filter: { labels: labelsOf(0), completed: 'hide' } })],
      columns: cols(
        { title: 'Eingegangen' },
        { title: 'Zu bestätigen', entryGate: gate('Für die Bestätigung braucht es Betrag und Eingangsdatum.', field(1, { op: 'filled' }), field(2, { op: 'filled' })) },
        { title: 'Bestätigung erstellt' },
        { title: 'Versendet', color: 'green', entryGate: gate('Bitte die Zuwendungsbestätigung als PDF anhängen.', { type: 'files', kind: 'pdf', includeComments: false, min: 1 }) },
        { title: 'Abgelegt', locked: true },
      ),
      labels: [
        { name: 'Bestätigung Pflicht', color: 'orange' },
        { name: 'Sammelbestätigung', color: 'blue' },
        { name: 'Dank offen', color: 'yellow' },
        { name: 'Großspende', color: 'purple' },
      ],
      fields: [
        { name: 'Spender*in', pinned: true, type: 'text', showOnCard: true },
        { name: 'Betrag', pinned: true, type: 'currency', showOnCard: true, config: sum },
        { name: 'Eingang am', type: 'date' },
        { name: 'Art', pinned: true, type: 'select', showOnCard: true, options: opts(['geld', 'Geldspende', 'green'], ['sach', 'Sachspende', 'blue'], ['verzicht', 'Aufwandsverzicht', 'purple']) },
        { name: 'Bestätigungsnr.', type: 'sequence', config: { sequence: { prefix: 'ZB-{JJJJ}-', digits: 3, yearly: true } } },
      ],
      automations: [
        { name: 'Über 300 € → Bestätigung Pflicht', trigger: { type: 'fieldChanged', fieldId: F(1) }, when: all(field(1, { op: 'gt', value: 300 })), actions: [{ type: 'addLabel', labelId: 'label:0' }] },
        { name: 'Ab 1.000 € → Großspende, Dank offen', trigger: { type: 'fieldChanged', fieldId: F(1) }, when: all(field(1, { op: 'gte', value: 1000 })), actions: [{ type: 'labels', add: labelsOf(3, 2), remove: [] }] },
        { name: 'Sachspende → Wertnachweis', trigger: fieldBecomes(3, 'sach'), actions: [{ type: 'addChecklist', items: ['Wert ermitteln (Rechnung oder Gutachten)', 'Alter und Zustand notieren', 'Herkunft: Privat- oder Betriebsvermögen'] }] },
        { name: 'Versendet → abhaken', trigger: { type: 'moved', columnId: C(3) }, actions: [{ type: 'complete' }] },
      ],
      cards: [
        c(0, 'Spende Familie Weber', [], { custom: { [F(0)]: 'Familie Weber', [F(1)]: 150, [F(3)]: 'geld' } }),
        c(1, 'Spende Autohaus Berg', [0, 3, 2], { custom: { [F(0)]: 'Autohaus Berg', [F(1)]: 2000, [F(3)]: 'geld' } }),
        c(1, 'Laptop für die Geschäftsstelle', [0], { custom: { [F(0)]: 'K. Sommer', [F(1)]: 420, [F(3)]: 'sach' } }),
        c(2, 'Fahrtkosten-Verzicht Trainer', [1], { custom: { [F(0)]: 'M. Yilmaz', [F(1)]: 360, [F(3)]: 'verzicht' } }),
      ],
    },
  },

  /* ---------- Beschaffung mit Freigabe ---------- */
  {
    id: 'builtin:purchasing',
    name: 'Beschaffung mit Freigabe',
    category: 'finanzen',
    summary: 'Bedarf, Vergleichsangebote, Freigabe ab Betrag, Bestellung und Lieferung',
    description:
      'Anschaffungen nachvollziehbar machen: Ab 1.000 € braucht es drei Vergleichsangebote, bevor eine Karte zur Freigabe darf. Freigeben darf nicht, wer den Bedarf angelegt hat; ab 2.500 € braucht es zwei Freigaben, und ändern sich Kosten oder Angebote, verfällt die Freigabe. Erst danach wird bestellt – mit Bestellnummer. Kommt die Lieferung nicht zum erwarteten Termin, gibt es eine Nachricht.',
    tags: ['Vier-Augen-Prinzip', 'Vergleichsangebote', 'Bestellnummer', 'Liefertermin'],
    color: '#0b7285',
    version: 2,
    adjust: ['Drei Vergleichsangebote ab 1.000 €', 'Zweite Freigabe ab 2.500 €'],
    data: {
      overview: { widgets: [metric(0, 'Kosten nach Spalte', 'column', 'open'), metric(0, 'Bestellt nach Monat', 'month', 'all'), wg('deadlines', 12)] },
      columns: cols(
        { title: 'Bedarf' },
        { title: 'Angebote einholen' },
        {
          title: 'Freigabe',
          entryGate: {
            when: all(field(0, { op: 'filled' }), anyOf(field(0, { op: 'lt', value: 1000 }), field(2, { op: 'gte', value: 3 }))),
            message: 'Bitte die geschätzten Kosten eintragen; ab 1.000 € braucht es drei Vergleichsangebote (Feld „Angebote“).',
            ownerOverride: true,
          },
        },
        { title: 'Bestellt', entryGate: gate('Bestellt wird erst nach der Freigabe.', field(3, { op: 'approved' })) },
        { title: 'Geliefert' },
        'Erledigt',
      ),
      labels: [
        { name: 'IT', color: 'blue' },
        { name: 'Ausstattung', color: 'green' },
        { name: 'Dienstleistung', color: 'purple' },
        { name: 'Eilt', color: 'red' },
      ],
      fields: [
        { name: 'Kosten (geschätzt)', pinned: true, type: 'currency', showOnCard: true, config: sum },
        { name: 'Lieferant', type: 'text' },
        { name: 'Angebote', pinned: true, type: 'number', config: { unit: 'Angebote', decimals: 0 } },
        { name: 'Freigabe', pinned: true, type: 'approval', showOnCard: true, config: approval({ notCreator: true, second: { fieldId: F(0), above: 2500 }, resetFields: [F(0), F(2)], resetFiles: true }) },
        { name: 'Bestellnr.', type: 'sequence', config: { sequence: { prefix: 'B-{JJJJ}-', digits: 3, yearly: true } } },
        { name: 'Lieferung erwartet', type: 'date' },
      ],
      cardTemplates: [
        {
          name: 'Bedarf',
          title: 'Bedarf: ',
          description: '<p><strong>Was und wofür:</strong> </p><p><strong>Menge:</strong> </p><p><strong>Bis wann nötig:</strong> </p>',
          checklist: ['Angebot 1', 'Angebot 2', 'Angebot 3'],
          assignCreator: true,
        },
      ],
      automations: [
        { name: 'Freigabe nötig: Verantwortliche informieren', trigger: { type: 'moved', columnId: C(2) }, actions: [tell('Eine Anschaffung wartet auf Freigabe.')] },
        { name: 'Freigegeben → bestellen', trigger: changed(3, { op: 'approved' }), when: all(inCols(2)), actions: [move(3, 'top')] },
        { name: 'Abgelehnt → zurück zu den Angeboten', trigger: changed(3, { op: 'rejected' }), actions: [move(1, 'top'), tell('Eine Anschaffung wurde nicht freigegeben.')] },
        {
          name: 'Lieferung überfällig → nachfragen',
          trigger: { type: 'dateReached', which: 'due', fieldId: F(5), offset: { mode: 'workdays', days: 1, time: '09:00' } },
          when: all(inCols(3)),
          actions: [{ type: 'addLabel', labelId: 'label:3' }, tell('Die erwartete Lieferung ist noch nicht da – bitte beim Lieferanten nachfragen.')],
        },
        { name: 'Geliefert → Wareneingang prüfen', trigger: { type: 'moved', columnId: C(4) }, actions: [{ type: 'addChecklist', items: ['Lieferung vollständig und unbeschädigt', 'Rechnung an die Buchhaltung', 'Inventarliste ergänzt'] }] },
        { name: 'Wareneingang fertig → erledigt', trigger: { type: 'checklistDone' }, when: all(inCols(4)), actions: [move(5)] },
        done(5),
      ],
      cards: [
        c(0, 'Beamer für den Seminarraum', [1], { custom: { [F(0)]: 900 } }),
        c(1, 'Neue Laptops Geschäftsstelle', [0], { custom: { [F(0)]: 3600, [F(2)]: 2 }, checklist: ['Angebot 1', 'Angebot 2', 'Angebot 3'] }),
        c(2, 'Reinigung Vereinsheim (Jahresvertrag)', [2], { custom: { [F(0)]: 1800, [F(1)]: 'Glanz & Co.', [F(2)]: 3 } }),
        c(5, 'Erste-Hilfe-Kasten', [1], { custom: { [F(0)]: 49.9 } }),
      ],
    },
  },
];

/* ---------- Persönlich: Steuererklärung ---------- */
export const TAX_RETURN_TEMPLATE: BuiltinTemplate = {
  id: 'builtin:taxreturn',
  name: 'Steuererklärung privat',
  category: 'persoenlich',
  summary: 'Belege sammeln, Fehlendes nachfordern, eintragen, abgeben – mit Summen je Anlage',
  description:
    'Die eigene Steuererklärung ohne Schuhkarton: Jeder Beleg oder Posten ist eine Karte mit absetzbarem Betrag und Anlage. Was noch fehlt, liegt in „Fehlt noch“ – hängst du die Datei an, ist der Posten vollständig. Beim Einrichten gibst du die Abgabefrist an; Summen zeigen, was je Spalte und Anlage zusammenkommt.',
  tags: ['Persönlich', 'Stichtag', 'Summen', 'Mit Beispielkarten'],
  color: '#495057',
  version: 2,
  cardsDefault: true,
  data: {
    setup: { label: 'Abgabefrist', help: 'Bis wann muss die Erklärung beim Finanzamt sein (ohne Steuerberatung meist der 31. Juli des Folgejahres)?' },
    overview: { widgets: [metric(0, 'Absetzbar nach Anlage', 'label', 'all', 12)] },
    columns: cols('Sammeln', 'Fehlt noch', 'Vollständig', 'Eingetragen', 'Erledigt'),
    labels: [
      { name: 'Werbungskosten', color: 'blue' },
      { name: 'Sonderausgaben', color: 'purple' },
      { name: 'Haushaltsnah', color: 'green' },
      { name: 'Außergewöhnliche Belastungen', color: 'orange' },
      { name: 'Kapitalerträge', color: 'sky' },
    ],
    fields: [
      { name: 'Betrag', pinned: true, type: 'currency', showOnCard: true, config: sum },
      { name: 'Anlage', pinned: true, type: 'select', showOnCard: true, options: opts(['n', 'Anlage N', 'blue'], ['sonder', 'Sonderausgaben', 'purple'], ['kap', 'Anlage KAP', 'sky'], ['v', 'Anlage V', 'orange'], ['haus', 'Haushaltsnahe Dienste', 'green'], ['ab', 'Außergewöhnl. Belastungen', 'yellow']) },
    ],
    automations: [
      { name: 'Beleg angehängt → vollständig', trigger: { type: 'fileAdded', kind: 'any', includeComments: false }, when: all(inCols(0, 1)), actions: [move(2)] },
      done(4),
    ],
    cards: [
      c(0, 'Lohnsteuerbescheinigung', [0], { dueFromSetup: -60, custom: { [F(1)]: 'n' } }),
      c(0, 'Fahrten zur Arbeit (Pendlerpauschale)', [0], { dueFromSetup: -45, custom: { [F(1)]: 'n' }, checklist: ['Arbeitstage zählen', 'Entfernung prüfen'] }),
      c(0, 'Homeoffice-Tage', [0], { dueFromSetup: -45, custom: { [F(1)]: 'n' } }),
      c(1, 'Spendenquittungen', [1], { dueFromSetup: -30, custom: { [F(1)]: 'sonder' } }),
      c(1, 'Handwerkerrechnungen', [2], { dueFromSetup: -30, custom: { [F(1)]: 'haus' } }),
      c(0, 'Versicherungsbeiträge', [1], { dueFromSetup: -30, custom: { [F(1)]: 'sonder' } }),
      c(0, 'Jahressteuerbescheinigung der Bank', [4], { dueFromSetup: -30, custom: { [F(1)]: 'kap' } }),
      c(0, 'Erklärung abschicken', [], { dueFromSetup: 0, after: [0, 1, 2, 3, 4, 5, 6] }),
    ],
  },
};
